// **Imports**
import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { InboxFilterType } from '../interfaces/admin-filter.interface';
import { AlphaHttpClient } from './alpha-http-client';
import { BranchService } from './branch.service';
import { ProjectService, UserGroup } from './project.service';
declare var alpha: any;

// **Service**
@Injectable({ providedIn: 'root' })
export class UserService {
    // **Dependencies**
    private httpClient = inject(HttpClient);
    private router = inject(Router);
    private projectService = inject(ProjectService);
    private alphaHttpClient = inject(AlphaHttpClient);
    private branchService = inject(BranchService);
    // **State**
    private userInfo$: Observable<any> | null = null;
    private _userInfo: UserInfoCombined = {} as any;

    private userMetadataSubject$: BehaviorSubject<IUserdata | null> = new BehaviorSubject<IUserdata | null>(null);
    private userMetadata$: Observable<IUserdata> | null = null;

    // Domain URL for AlphaHttpClient
    private adminDomainUrl = {
        type: 'environment',
        value: {
            __alpha_env__: 'ALPHA_ADMIN_SERVICE_URL',
        },
    };

    public baseURL!: string;

    // **Public Methods**
    getUser(): Observable<UserInfoCombined> {
        return this.getUserInfo();
    }

    getGroupInfo(): string[] {
        return this._userInfo?.groupInfo || [];
    }

    getUserMetadata(): UserMetadata {
        return this._userInfo?.userMetadata?.[0] || {};
    }

    getStoredUserInfo(): UserInfoCombined {
        return this._userInfo;
    }

    updateUserMetadata(metadata: Record<string, any>): Observable<any> {
        return this.httpClient
            .post(`${window.environments.ALPHA_UI_AUTH_SERVICE_URL}/user/metadata`, { metadata })
            .pipe(
                tap(() => {
                    this._userInfo.userMetadata[0].metadata = metadata;
                }),
                catchError((e) => this.handleError(e)),
            );
    }

    logOut(path?: string): void {
        const projectId = this.projectService.fetchCurrentProject()?.projectId;
        const branch = this.branchService.getCurrentBranch();
        let redirectUrl: string;
        if (path) {
            // Ensure branch is preserved in the provided path
            redirectUrl = this.ensureBranchInUrl(path, branch);
        } else {
            redirectUrl = `${window.environments.SELF_URL}/home/cases?projectId=${projectId}&branch=${branch}`;
        }
        window.location.href = `${window.environments.ALPHA_UI_AUTH_SERVICE_URL}/auth/logout?redirectUrl=${encodeURIComponent(redirectUrl)}`;
    }

    /**
     * Ensures the branch query param is present in the URL.
     * If branch param already exists, it's preserved. Otherwise, it's added.
     */
    private ensureBranchInUrl(url: string, branch: string): string {
        try {
            const urlObj = new URL(url);
            if (!urlObj.searchParams.has('branch')) {
                urlObj.searchParams.set('branch', branch);
            }
            return urlObj.toString();
        } catch {
            // If URL parsing fails, return as-is
            return url;
        }
    }

    getIsManagerInfo(): Observable<IManager> {
        return this.getUser().pipe(
            switchMap((data) =>
                this.alphaHttpClient.post<IManager>(this.adminDomainUrl, `/user/is-manager`, {
                    username: data.userInfo.username,
                }),
            ),
        );
    }

    getUserInfo(): Observable<UserInfoCombined> {
        if (!this.userInfo$) {
            this.userInfo$ = forkJoin({
                userInfo: this.httpClient.get<UserAPIResponse>(
                    `${window.environments.ALPHA_UI_AUTH_SERVICE_URL}/auth/user-info`,
                ),
                userMetadata: this.fetchUserMetadata(),
                groupInfo: this.fetchGroupInfo(),
            }).pipe(
                tap(() => console.log('forkJoin completed')),
                map(({ userInfo, userMetadata, groupInfo }) => {
                    this._userInfo = {
                        userInfo: userInfo?.response,
                        userMetadata: userMetadata?.metadata,
                        groupInfo: groupInfo?.map((v: GroupInfo) => v.group_name).filter(Boolean),
                    };
                    return this._userInfo;
                }),
                tap((result) => this.updateUserInfoToLocal(result)),
                catchError((e) => this.handleError(e)),
                shareReplay(1),
            );
        }
        return this.userInfo$;
    }

    // **Private Methods**
    public fetchUserMetadata() {
        return this.httpClient
            .get<any>(`${window.environments.ALPHA_UI_AUTH_SERVICE_URL}/user/metadata`)
            .pipe(catchError((e) => this.handleError(e)));
    }

    private fetchGroupInfo(): Observable<GroupInfo[]> {
        return this.httpClient
            .get<{
                status: number;
                groups: GroupInfo[];
            }>(`${window.environments.ALPHA_UI_AUTH_SERVICE_URL}/auth/group-info`)
            .pipe(
                map((response) => response.groups),
                catchError((e) => this.handleError(e)),
            );
    }

    private updateUserInfoToLocal(userData: UserInfoCombined) {
        if (!window.alpha?.local) {
            console.warn(new Error('alpha.local does not exist. Could not set alpha.local.userInfo'));
            return;
        }
        if (userData && !window.alpha.local.get().userInfo) {
            userData.userInfo.alphaGroupInfo = userData.groupInfo;
            userData.userInfo.alphaUserMetadata = userData.userMetadata;
            this.updateUserToLocal();
        }
    }

    updateUserToLocal() {
        if (!window.alpha?.local) {
            console.warn(new Error('alpha.local does not exist. Could not set alpha.local.userInfo'));
            return;
        }
        const localMappingSource = window.alpha.local.get();
        if (this._userInfo?.userInfo) {
            localMappingSource.userInfo = this._userInfo?.userInfo;
            localMappingSource.userInfo.alphaGroupInfo = this._userInfo?.groupInfo;
            localMappingSource.userInfo.alphaUserMetadata = this._userInfo?.userMetadata;
        }
    }

    private handleError(exception: HttpErrorResponse): Observable<any> {
        if (exception.status === 401) {
            if (exception?.error?.message === 'FORBIDDEN' || exception?.error?.error.message === 'FORBIDDEN') {
                this.router.navigate(['unauthorized'], {
                    queryParams: { redirectUrl: encodeURIComponent(window.location.href) },
                });
                return of(null);
            } else {
                const url = new URL(window.location.href);
                const projectId = url.searchParams.get('projectId');
                const branch = url.searchParams.get('branch') || 'main';
                const redirectUrl = encodeURIComponent(
                    `${window.environments.SELF_URL}/home/cases?projectId=${projectId}&branch=${branch}`,
                );
                window.location.href = `${window.environments.ALPHA_UI_AUTH_SERVICE_URL}/auth/login?redirectUrl=${redirectUrl}`;
            }
        }
        return throwError(() => new Error('An error occurred. Please try again later.'));
    }

    fetchAllGroups(): Observable<UserGroupsResponse> | any {
        return this.alphaHttpClient.get<UserGroupsResponse>(this.adminDomainUrl, `/group/fetch-all`);
    }

    fetchUser(): Observable<IUserdata> {
        if (!this.userMetadata$) {
            this.userMetadata$ = this.alphaHttpClient
                .post<IUserdata>(this.adminDomainUrl, `/user/fetch`, {
                    userName: this._userInfo?.userInfo?.username || '',
                })
                .pipe(
                    tap((data) => this.userMetadataSubject$.next(data)),
                    shareReplay(1),
                );
        }

        return this.userMetadata$;
    }

    fetchSessionMaxAge(): Observable<{ sessionMaxAge: number; sessionTimeout: number }> | any {
        return this.httpClient.get<{ sessionMaxAge: number; sessionTimeout: number }>(
            `${this.baseURL}/auth/fetch-cookie-maxage`,
        );
    }

    userLogOut(): void {
        window.location.href = `${this.baseURL}/auth/logout?redirectUrl=${encodeURIComponent(window.location.href)}`;
    }

    userLogin(): void {
        window.location.href = `${this.baseURL}/auth/login?redirectUrl=${encodeURIComponent(window.location.href)}`;
    }

    resfreshCookie() {
        return this.httpClient.post(`${this.baseURL}/auth/refresh-cookie`, {});
    }
}

// **Interfaces**
// User Info Interfaces
interface SubInfo {
    accountId: string;
    strategy: string;
}

interface Metadata {
    country: string;
    pincode: number;
}

interface Subscription {
    organisationId: string;
    type: string;
    start_date: string;
    expiry_date: string;
    maxNumberOfApps: string;
    disabled: boolean;
    status: string;
    description: string;
    ui_config: null;
}

interface AdditionalInfo {
    id: string;
    userKey: null;
    organisationId: string;
    createdAt: string;
    contactNumber: string;
    metadata: Metadata;
    invalidAttempt: string;
    tempPasswordFlag: boolean;
    publicUser: boolean;
    subscription: Subscription;
}

export interface GroupInfo {
    id: number;
    group_name: string;
}

export interface UserInfo {
    sub: SubInfo;
    displayName: string;
    firstName: string;
    lastName: string;
    username: string;
    groupList: any[];
    organisationId: string;
    additional: AdditionalInfo;
    roles: any[];
    teams: any[];
    alphaUserMetadata: any;
    alphaGroupInfo: string[];
}

export interface UserAPIResponse {
    response: UserInfo;
    status: number;
}

interface UserInfoCombined {
    userInfo: UserInfo;
    userMetadata: any;
    groupInfo: string[];
}

interface UserMetadata {
    metadata: {
        enquiryInbox: any;
        savedFilters: ISavedFilters;
        defaultTask: any;
        enquiryDefaultTask: any;
        [key: string]: any;
    };
}

// Filter Interfaces
export interface ISavedFilters {
    [uuid: string]: ITaskContainer;
}

export interface ITaskContainer {
    [taskType: string]: ITask[];
}

export interface ITask {
    name: string;
    key: string;
    default: boolean;
    filterObject: IFilterObject;
    filterId?: string;
    type?: InboxFilterType;
}

export interface IManager {
    status: number;
    isManager: boolean;
}

export interface IFilterObject {
    [filterKey: string]: IFilter;
}

export interface IFilter {
    type: string;
    mapping: string;
    mappingType: string;
    label: string;
    value?: string | null;
    minLabel?: string;
    maxLabel?: string;
    [additionalProperties: string]: any;
}

export interface IDateRangeFilter extends IFilter {
    type: 'date-range';
    minLabel?: string;
    maxLabel?: string;
}

export interface ISearchFilter extends IFilter {
    type: 'search';
    value?: string | null;
}

export interface IEnumFilter extends IFilter {
    type: 'enum';
    [option: string]: boolean | string | null | undefined;
}

export interface INumberRangeFilter extends IFilter {
    type: 'number-range';
    minLabel: string;
    maxLabel: string;
}

export type FilterType = IDateRangeFilter | ISearchFilter | IEnumFilter | INumberRangeFilter;

export interface UserGroupsResponse {
    groups: UserGroup[];
}

export interface PaginatedGroupsResponse {
    groups: UserGroup[];
    totalCount: number;
}

export interface IUserdata {
    status: HttpStatusCode;
    umas: {
        groups: UserGroup[];
        metadata: Metadata;
    }[];
}

interface Metadata {
    skills: string[];
    isActive: boolean;
    bandwidth: number;
    dailyLimit: number;
    remainingBandwidth: number;
    remainingDailyLimit: number;
    enableTaskDistribution: boolean;
    enquiryInbox: Record<string, any>;
    defaultTask: Record<string, any>;
    savedFilters: Record<string, any>;
    adminSavedFilters: Record<string, any>;
    adminDefaultTask: Record<string, any>;
}
